import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, Row, Col } from 'antd';
import autobind from 'autobind-decorator';

import { TextView } from 'src/core/ui/text.view';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { BooleanView } from 'src/core/ui/boolean.view';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';
import { CardController } from 'src/app/shared/card.controller';
import { QuoteEntryList } from 'src/app/accounting/suppliers/cost/parts/quote-entry-list';

interface Props extends ComponentViewProps<DataSet<QuoteEntrySummary>> {
	filters?: SupplierCostFilters;
	invoiceDate?: Date;
	invoiceReferenceNumber?: string;
	payloadDataSet?: DataSet<SelectableInvoiceEntryPayload>;
	onChangeFilter: (fieldName: keyof SupplierCostFilters, value?: any) => void;
	onApplyFilters: () => void;
	onChangeInvoiceDate: (invoiceDate: Date) => void;
	onChangeInvoiceReferenceNumber: (invoiceReferenceNumber: string) => void;
	onChangeSelection: (selectedQuoteEntryIds: string[]) => void;
	onGenerateInvoice: () => Promise<void>;
	onAdjustSale: (salesAdjustment: SalesAdjustmentProductManually) => void;
}

@observer
export class SupplierCostFormView extends React.Component<Props, {}> {
	@autobind
	private handleSupplierChange(suppliers: SupplierSummary[]): void {
		this.props.onChangeFilter('suppliersId', suppliers != null && suppliers.length > 0 ? suppliers.map(m => m.id) : null);
	}

	render(): React.ReactNode {
		const {
			model, filters, onChangeFilter, onApplyFilters,
			invoiceDate, invoiceReferenceNumber, onChangeInvoiceDate, onChangeInvoiceReferenceNumber,
			payloadDataSet, onAdjustSale, onChangeSelection, onGenerateInvoice, setBusy
		} = this.props;

		const selectedPayloads = model.items
			.filter(m => (payloadDataSet.items.find(p => p.quoteEntryId === m.id) || { checked: false }).checked)
		const canGenerate = invoiceReferenceNumber.trim() != ''
			&& selectedPayloads.length > 0
			&& selectedPayloads.every(m => m.supplierId === selectedPayloads[0].supplierId);

		return (
			<div className="window window-normal">
				<div className="panel">
					<h2>Invoice Products</h2>
					<Row gutter={16}>
						<Col xs={6}>
							<SupplierEditorController
								multi
								mode="edit"
								label="Supplier"
								value={filters.suppliersId}
								onChange={this.handleSupplierChange}
								setBusy={this.props.setBusy}
							/>
						</Col>
						<Col xs={6}>
							<BooleanView
								mode="edit"
								label="Show Invoiced Products"
								value={filters.showInvoiced}
								onChanged={(value) => onChangeFilter('showInvoiced', value)}
							/>
						</Col>
						<Col xs={6}>
							<BooleanView
								mode="edit"
								label="Show Confirmed Only"
								value={filters.showConfirmedOnly}
								onChanged={(value) => onChangeFilter('showConfirmedOnly', value)}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Reservation Date"
								value={filters.reservationDate}
								onChanged={(date) => onChangeFilter('reservationDate', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Booking Reference"
								value={filters.quoteNumber}
								onChanged={(value) => onChangeFilter('quoteNumber', value)}
							/>
						</Col>
						<Col xs={6}>
							<DropdownView
								label="Booking Status"
								model={filters.quoteStatus}
								options={[
									{ value: 1, label: 'Open' },
									{ value: 2, label: 'Confirmed' },
									{ value: 3, label: 'Closed' },
									{ value: 4, label: 'Cancelled' }
								]}
								onChanged={(option) => onChangeFilter('quoteStatus', option != null ? option.value : null)}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Service From"
								value={filters.serviceDateFrom}
								onChanged={(date) => onChangeFilter('serviceDateFrom', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Service To"
								value={filters.serviceDateTo}
								onChanged={(date) => onChangeFilter('serviceDateTo', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Confirmation Reference"
								value={filters.confirmationReferenceNumber}
								onChanged={(value) => onChangeFilter('confirmationReferenceNumber', value)}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={8}>
							<Button
								icon="search"
								style={{ margin: '8px auto' }}
								onClick={onApplyFilters}>
								Search
							</Button>
						</Col>
					</Row>
					<hr />
					<Row gutter={16}>
						<Col xs={14}>
						</Col>
						<Col xs={4}>
							<TextView
								required
								label="Invoice Reference Number"
								mode="edit"
								value={invoiceReferenceNumber}
								onChanged={(value) => {
									onChangeInvoiceReferenceNumber(value);
								}}
							/>
						</Col>
						<Col xs={3}>
							<DateView
								required
								label="Invoice Date"
								value={invoiceDate}
								onChanged={(value) => {
									onChangeInvoiceDate(value);
								}}
								mode="edit"
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={3}>
							<Button
								icon="reload"
								className="accounting-generate-btn"
								disabled={!canGenerate}
								onClick={onGenerateInvoice}>
								Generate
							</Button>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24}>
							<CardController
								icon="barcode"
								title="Reservations"
								className="card-borderless"
								mode="display">
								<QuoteEntryList
									model={model}
									payloadDataSet={payloadDataSet}
									onChangeSelection={onChangeSelection}
									onAdjustSale={onAdjustSale}
									setBusy={setBusy}
								/>
							</CardController>
						</Col>
					</Row>
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/cost/supplier-cost-form.view.tsx